import { MutationCache, QueryClient } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { toast } from 'sonner';
import superjson from 'superjson';

import { getApiBaseUrl } from './api-url';
import { getOfflineActorUserId } from './offline-actor';
import {
  queueNavioBuildOfflineMutation,
  shouldQueueNavioBuildOfflineMutation,
} from './offline-mutation-policy';

import type { AppRouter } from '@navioworks/api';

export const trpc: ReturnType<typeof createTRPCReact<AppRouter>> = createTRPCReact<AppRouter>();

const getApiUrl = () => `${getApiBaseUrl()}/trpc`;

function browserOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError(_error, variables, _context, mutation) {
      const mutationKey = mutation.options.mutationKey;
      if (
        !shouldQueueNavioBuildOfflineMutation({
          isOnline: browserOnline(),
          error: _error,
          mutationKey,
        })
      ) {
        return;
      }

      const actorUserId = getOfflineActorUserId();
      if (!actorUserId) return;

      queueNavioBuildOfflineMutation({ mutationKey, variables, actorUserId })
        .then((queued) => {
          if (queued) toast.info('Saved to offline queue');
        })
        .catch((queueError) => {
          console.error('Failed to queue offline NavioBuild mutation', queueError);
        });
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Supabase token getter — set by AuthProvider on mount
let supabaseGetToken: (() => Promise<string | null>) | null = null;

export function setSupabaseTokenGetter(getter: () => Promise<string | null>) {
  supabaseGetToken = getter;
}

function idempotencyKeyFromHeaderOptions(options: unknown): string | null {
  const opList = (options as { opList?: Array<{ context?: Record<string, unknown> }> }).opList;
  const key = Array.isArray(opList) && opList.length === 1 ? opList[0]?.context?.['idempotencyKey'] : null;
  return typeof key === 'string' && key.trim() ? key : null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const trpcClient: any = trpc.createClient({
  links: [
    httpBatchLink({
      url: getApiUrl(),
      // tRPC v11 typing quirk: the link's transformer slot doesn't pick up the
      // router's transformer config, so a cast is required (matches web + fit).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformer: superjson as any,
      async headers(options) {
        try {
          const baseHeaders: Record<string, string> = {};
          if (supabaseGetToken) {
            const accessToken = await supabaseGetToken();
            if (accessToken) {
              baseHeaders['authorization'] = `Bearer ${accessToken}`;
            }
          }
          const idempotencyKey = idempotencyKeyFromHeaderOptions(options);
          if (idempotencyKey) {
            baseHeaders['x-idempotency-key'] = idempotencyKey;
          }
          return baseHeaders;
        } catch (error) {
          console.error('Error in tRPC headers:', error);
          return {};
        }
      },
    }),
  ],
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TRPCProvider: any = trpc.Provider;
